package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** Aggregate reports intentionally contain no project, person, cell or image identity. */
@Serializable
data class ShotAuditReportDto(
    val schemaVersion: Int = 1,
    val generatedAt: String,
    val coverage: ShotReportCoverageDto,
    val freshness: ShotReportFreshnessDto,
    val approvals: ShotReportApprovalsDto,
    val storeParity: ShotReportParityDto,
)

/** Planned dimensions are counts, never slot names, locale codes or device identifiers. */
@Serializable
data class ShotReportCoverageDto(val slots: Int, val locales: Int, val devices: Int, val cells: Int)

/** These are capture coverage states, including missing volume bytes, rather than render scores. */
@Serializable
data class ShotReportFreshnessDto(val current: Int, val stale: Int, val missing: Int)

/** An absent approval is different from consent that no longer matches its immutable items. */
@Serializable
enum class ShotReportApprovalStatus { NONE, VALID, INVALID }

/** Only the newest unrevoked approval is summarized; no approver or render identity is exposed. */
@Serializable
data class ShotReportApprovalsDto(val status: ShotReportApprovalStatus, val items: Int, val invalidItems: Int)

/** Unknown remains explicit when independently recorded store read-back is unavailable. */
@Serializable
enum class ShotReportParityStatus { UNKNOWN, VERIFIED, MISMATCH }

/** A prior delivery selection is not evidence for the whole listing or the live store today. */
@Serializable
enum class ShotReportParityScope { LAST_DELIVERY_SELECTION }

/** Counts describe stored independent read-back only, with its observation time when known. */
@Serializable
data class ShotReportParityDto(
    val status: ShotReportParityStatus = ShotReportParityStatus.UNKNOWN,
    val scope: ShotReportParityScope = ShotReportParityScope.LAST_DELIVERY_SELECTION,
    val sets: Int = 0,
    val verifiedSets: Int = 0,
    val mismatchedSets: Int = 0,
    val unknownSets: Int = 0,
    val checkedAt: String? = null,
)

/** Expiring links preserve the workspace's control without introducing a paid access gate. */
@Serializable
data class CreateShotReportShareRequest(val expiresInDays: Int = 30)

/** Authenticated management metadata never contains a token verifier or a reusable bearer URL. */
@Serializable
data class ShotReportShareDto(
    val id: String,
    val createdAt: String,
    val expiresAt: String,
    val revokedAt: String? = null,
)

/** The bearer is returned once to its issuer, and diagnostic stringification must redact it. */
@Serializable
data class CreatedShotReportShareDto(
    val share: ShotReportShareDto,
    val token: String,
    val reportPath: String,
    val badgePath: String,
) {
    override fun toString(): String = "CreatedShotReportShareDto(share=$share, token=[redacted], reportPath=[redacted], badgePath=[redacted])"
}
